<template>
	<div>
		<div class="row content-header">
			<div class="content-header-left mb-2 col-md-9 col-12">
				<div class="row breadcrumbs-top">
					<div class="col-12">
						<h2 class="content-header-title float-left pr-1 mb-0">Economic Report</h2>
					</div>
				</div>
			</div>
		</div>

		<!-- Table Container Card -->
		<b-card no-body>
			<div class="m-3">
				<b-row>
					<!-- Date picker -->
					<b-col cols="12" md="7">
						<b-row>
							<b-col md="5">
								<label for="example-input">From Date</label>
								<b-form-datepicker
									id="from-date"
									:date-format-options="{
										year: 'numeric',
										month: 'short',
										day: '2-digit',
										weekday: 'short',
									}"
									placeholder="Choose a date"
									local="en"
									v-model="from_date"
								/>
							</b-col>
							<b-col md="5">
								<label for="example-input">To Date</label>
								<b-form-datepicker
									id="to-date"
									:date-format-options="{
										year: 'numeric',
										month: 'short',
										day: '2-digit',
										weekday: 'short',
									}"
									placeholder="Choose a date"
									local="en"
									v-model="to_date"
								/>
							</b-col>
							<b-col md="2 d-flex align-items-end">
								<b-button @click="economicReport()" variant="primary "> Filter </b-button>
							</b-col>
						</b-row>
					</b-col>
					<b-col cols="12" md="5 p-0" class="px-2">
						<b-row>
							<b-col cols="10 ">
								<b-form-group label="Flock Id">
									<v-select
										v-model="selected_flock_id"
										:options="flock_id"
										label="flock_id"
										:searchable="true"
										:clearable="false"
										@input="getEconomicReports()"
									/>
								</b-form-group>
							</b-col>
							<!-- <b-col cols="2" class="d-flex justify-content-center align-items-center mt-1">
								<b-button @click="economicReport()" variant="warning">
									<feather-icon icon="DownloadIcon" size="16" />
								</b-button>
							</b-col> -->
						</b-row>
					</b-col>
				</b-row>
			</div>
			<div v-if="getEconomicReport && getEconomicReport.length > 0">
				<b-table
					:items="getEconomicReport"
					responsive
					:fields="fields"
					show-empty
					empty-text="No matching records found"
					class="position-relative"
				>
					<template #cell(index)="data">
						<div class="ml-1">
							{{ data.index }}
						</div>
					</template>
				</b-table>
				<!-- <div class="mt-2 mb-1">
					<b-pagination
						:value="getEconomicReport.current_page"
						:total-rows="getEconomicReport.total"
						:per-page="getEconomicReport.per_page"
						align="right"
						@change="economicReport"
					>
					</b-pagination>
				</div> -->
			</div>
			<div v-else>
				<b-row class="report-view">
					<b-col md="12" class="d-flex justify-content-center flex-column align-items-center">
						<div class="circle">
							<b-img
								:src="require('@/assets/images/svg/reports/internal-report.svg')"
								style="width: 70px; height: 70px"
							/>
						</div>
						<h3 class="mt-5 ml-2 textInfo">No Economic Report Found</h3>
					</b-col>
				</b-row>
			</div>
		</b-card>
	</div>
</template>

<script>
import axiosIns from "@/libs/axios";
import { BASE_URL } from "@core/common/constants";
import moment from "moment";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
	components: {
		vSelect,
	},
	data() {
		return {
			flock_id: [],
			from_date: null,
			selected_flock_id: null,
			source: [],
			to_date: null,
			fromDate: null,
			toDate: null,
			getEconomicReport: {},
			search_filter: {
				input_field: "",
			},
		};
	},
	created: function () {
		this.from_date = moment().clone().startOf("month").format("YYYY-MM-DD");
		this.to_date = moment().clone().endOf("month").format("YYYY-MM-DD");
	},
	mounted: function () {
		// Fetching in mounted(), not created() — on a hard page load (not a
		// same-app SPA route change) the axios call from created() was
		// intermittently firing before something upstream (layout/navbar)
		// finished its own init, and the request just silently never went
		// out. mounted() guarantees this component's own render pass has
		// completed at least once, which was reliable in testing where
		// created() was not.
		this.economicReport();
	},

	computed: {
		// production_type_id comes from the selected flock's own record
		// (eager-loaded as currentReport by the backend), not a static
		// assumption — so this always matches the flock actually on screen.
		productionTypeId() {
			return this.getEconomicReport?.[0]?.currentReport?.production_type_id ?? 1;
		},
		// "Total Chick Sale" is a broiler concept (live-bird weight sale) —
		// Layer/Breeder companies never populate it and don't sell chicks by
		// weight, so showing that column there was a leftover broiler-only
		// artifact. Swap it for "Total Egg Sale" (now returned by the
		// backend as total_egg_sale) for those two production types instead.
		fields() {
			const zeroIfNull = (value) => (value === null ? 0 : value);
			const base = [
				{
					key: "date",
					label: "Date",
					formatter: (value) => {
						if (!value) return "—";
						const m = moment(value, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601], true);
						return m.isValid() ? m.format("MMMM Do YYYY") : moment(value).format("MMMM Do YYYY");
					},
				},
				{ key: "total_birds", label: "Total Bird" },
				{ key: "cost_of_chick", label: "Chick Cost", formatter: zeroIfNull },
				{ key: "cost_of_housekeeping", label: "Housekeeping Cost", formatter: zeroIfNull },
				{ key: "cost_of_labour", label: "Labour Cost", formatter: zeroIfNull },
				{ key: "other_cost", label: "Other Cost", formatter: zeroIfNull },
				{ key: "cost_of_vaccination", label: "Vaccine Cost", formatter: zeroIfNull },
				{ key: "cost_of_medicine", label: "Medicine Cost", formatter: zeroIfNull },
				{ key: "total_cost", label: "Total Cost", formatter: zeroIfNull },
			];
			const revenueField =
				this.productionTypeId === 1
					? { key: "total_chick_sale", label: "Total Chick Sale", formatter: zeroIfNull }
					: { key: "total_egg_sale", label: "Total Egg Sale", formatter: zeroIfNull };
			return [...base, revenueField, { key: "profit_per_chick", label: "Profit Per Chick", formatter: zeroIfNull }];
		},
	},

	methods: {
		moment() {
			return moment();
		},
		economicReport() {
			// This page has no farmId anywhere (not in the route, no farm
			// selector in the UI) — was calling
			// web/farm/${undefined}/flock-list/economic, which always came
			// back empty, so the Flock Id dropdown below could never
			// populate and a report could never be selected. Report is
			// flock-wise by design (owner picks a flock, not a farm), so
			// list every flock across the whole company instead.
			axiosIns
				.get("web/get-all-flock-list")
				.then((response) => {
					this.flock_id = response.data.flock_list;
				})
				.catch((error) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text:
								error.response?.data?.message ||
								error.response?.data?.error ||
								(error.response?.data?.errors
									? Object.values(error.response.data.errors).flat().join(", ")
									: "Something went wrong. Please try again."),
						},
					});
				});
		},
		calculateIndex(index) {
			return index + 1;
		},
		getEconomicReports() {
			if (this.selected_flock_id != null) {
				const flockId = this.selected_flock_id.flock_id;
				let url = `web/flock/${flockId}/economic`;
				axiosIns
					.get(url)
					.then((response) => {
						this.getEconomicReport = response.data;
					})
					.catch((error) => {
						this.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: {
								icon: "CoffeeIcon",
								variant: "danger",
								text:
									error.response?.data?.message ||
									error.response?.data?.error ||
									(error.response?.data?.errors
										? Object.values(error.response.data.errors).flat().join(", ")
										: "Something went wrong. Please try again."),
							},
						});
						const data = error.response?.data?.message;
						let arr = Object.values(data).flat().toString(",");
						this.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: {
								icon: "CoffeeIcon",
								variant: "danger",
								text: arr,
							},
						});
					});
			} else {
				this.economicReport();
			}
		},
		// excelDownload() {
		// 	const reportType = "current";
		// 	if (this.search_filter.from_date && this.search_filter.to_date) {
		// 		this.search_filter.from_date = moment(this.search_filter.from_date).format("YYYY-MM-DD");
		// 		this.search_filter.to_date = moment(this.search_filter.to_date).format("YYYY-MM-DD");
		// 	}
		// 	let order_filter_data = this.prepareForm(this.search_filter);
		// 	axiosIns
		// 		.post(`web/export-expense-entry/${reportType}`, order_filter_data)
		// 		.then((response) => {
		// 			var tempLink = document.createElement("a");
		// 			tempLink.style.display = "none";
		// 			tempLink.href = response.data.file;
		// 			tempLink.setAttribute("download", response.data.name);
		// 			if (typeof tempLink.download === "undefined") {
		// 				tempLink.setAttribute("target", "_blank");
		// 			}
		// 			document.body.appendChild(tempLink);
		// 			tempLink.click();
		// 			document.body.removeChild(tempLink);
		// 			this.getSourceList();
		// 		})
		// 		.catch((error) => {
		// 			error.response;
		// 			const data = error.response?.data?.errors;
		// 			data, "data";
		// 			let arr = Object.values(data).flat().toString(",");
		// 			arr, "array";
		// 			this.$toast({
		// 				component: ToastificationContent,
		// 				position: "top-right",
		// 				props: {
		// 					icon: "CoffeeIcon",
		// 					variant: "danger",
		// 					text: arr,
		// 				},
		// 			});
		// 			this.search_filter.to_date = null;
		// 			this.search_filter.from_date = null;
		// 			this.search_filter.input_field == null;
		// 			this.getSourceList();
		// 		});
		// },
	},
};
</script>

<style lang="scss" scoped>
.itemText {
	color: black;
	font-weight: 400;
}

.itemHeading {
	color: #296db4;
	font-weight: 400;
	font-size: 14px;
}

.report-view {
	background-color: #f7f7f7;
	border-radius: 5px;
	padding: 2rem 2rem;
	margin: 3rem 3rem;
	height: 400px;
}

.circle {
	width: 195px;
	height: 195px;
	border-radius: 50%;
	background: #ffffff 0% 0% no-repeat padding-box;
	border: 2px solid #296db4;
	opacity: 1;
	display: flex;
	justify-content: center;
	align-items: center;
}

.textInfo {
	color: rgba(41, 109, 180, 1);
	font-size: 20px;
	font-weight: 600;
}

.underline li {
	font-size: 15px;
	list-style-type: none;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
